import { roundedCornerRadiusFromPercent } from '@/lib/object-effects/rounding'
import type { WrapBand } from '@/lib/text/types/wrap-band'

/**
 * Horizontal inset the box's own rounded corner imposes on a text row spanning
 * `[rowTop, rowBottom)`, in content-local px — the CONTAINMENT counterpart to the exclusion bands
 * above. Symmetric: the same inset applies to both edges, since all four corners share one radius.
 *
 * This is the rounded-rect analogue of `scanlineSegments`/`intersectSegments` in
 * `lib/shapes/shape-text.ts`: a row must fit inside the outline across its WHOLE height, so the
 * binding constraint is whichever of its two edges sits nearer a corner — `rowTop` for the top
 * corners, `rowBottom` for the bottom ones — and both are taken because a short frame can be
 * pinched from above and below at once. Closed-form rather than sampled, so there is no staircase
 * approximation: at 100% on a square (r = w/2 = h/2) it reduces exactly to the circle's half-chord
 * `r - sqrt(r² - (y - r)²)`, which is precisely what a circle shape's scanline yields for the same
 * row — so a fully-rounded square text box and a circle of the same size lay text out identically.
 *
 * `contentWidth`/`contentHeight` are the box's real content area, and the radius is derived from
 * them directly (treating the content box as proportionally rounded). That is marginally more
 * conservative than offsetting the true outer curve inward by the few-px CSS padding, never less —
 * so it cannot reintroduce clipping.
 */
export function roundedBoxRowInset(
  contentWidth: number,
  contentHeight: number,
  roundedCornerPercent: number | undefined,
  rowTop: number,
  rowBottom: number
): number {
  if (typeof roundedCornerPercent !== 'number' || roundedCornerPercent <= 0) return 0

  const w = Math.max(0, contentWidth)
  const h = Math.max(0, contentHeight)
  const r = roundedCornerRadiusFromPercent(w, h, roundedCornerPercent)

  if (r <= 0) return 0

  // Depth of each row edge into its nearest corner's arc region; <= 0 means that edge has already
  // cleared the arc and imposes nothing.
  const topDepth = r - Math.max(0, rowTop)
  const bottomDepth = r - Math.max(0, h - Math.min(h, rowBottom))
  const insetAt = (depth: number): number =>
    depth <= 0 ? 0 : r - Math.sqrt(Math.max(0, r * r - depth * depth))

  return Math.min(w / 2, Math.max(insetAt(topDepth), insetAt(bottomDepth)))
}

/**
 * The rounded corner's contribution to one row's free horizontal span, expressed as the same
 * `WrapBand` pair the row walker already complements — one band pinned to each edge. Returned
 * per-row (not as a static set) because the inset varies continuously down the frame; see
 * `roundedBoxRowInset`.
 */
export function roundedBoxRowBands(
  contentWidth: number,
  contentHeight: number,
  roundedCornerPercent: number | undefined,
  rowTop: number,
  rowBottom: number
): WrapBand[] {
  const inset = roundedBoxRowInset(
    contentWidth,
    contentHeight,
    roundedCornerPercent,
    rowTop,
    rowBottom
  )

  if (inset <= 0) return []
  const w = Math.max(0, contentWidth)

  return [
    { top: rowTop, bottom: rowBottom, left: 0, right: inset },
    { top: rowTop, bottom: rowBottom, left: w - inset, right: w },
  ]
}
